import { ThemeProvider } from "@mui/material";
import BrowserGuard from "components/BrowserGuard";
import ReactDOM from "react-dom/client";
import { Provider } from "react-redux";
import { Slide, ToastContainer } from "react-toastify";
import { PersistGate } from "redux-persist/integration/react";
import "regenerator-runtime/runtime";
import { initSentryFromFeatureFlags, isLocalHostname, isLocalSentryOptIn } from "services/monitoring";
import "styles/index.css";
import { createCustomTheme } from "styles/theming/theme";
import App from "./App";
import ErrorHandler from "./components/ErrorHandler";
import { FeatureFlagsContext, fetchFeatureFlagData } from "./contexts/FeatureFlagsContext";
import { persistor, store } from "./redux/store";

const root = ReactDOM.createRoot(document.getElementById("root")!);
const theme = createCustomTheme();

// Load the feature flag data from the backend while the browser is loading the
// application. This read-only data will be served by the feature flag context
// provider to any component that needs it.
const ffData = await fetchFeatureFlagData();

// Initialize monitoring as early as possible. Sentry is lazy-loaded only when enabled via
// feature flags, and is skipped on localhost so ordinary local development never reports.
//
// Local verification opts in with VITE_SENTRY_LOCAL=true in webapp/ui/.env.local (restart the
// dev server after changing it), which starts Sentry with replay off. VITE_SENTRY_REPLAY=true
// implies it and additionally forces both replay sample rates to 1.0 - see isLocalSentryOptIn
// and isLocalReplayOptIn in services/monitoring.
const hostname = typeof window !== "undefined" ? window.location?.hostname : "";
if (!isLocalHostname(hostname) || isLocalSentryOptIn()) {
  await initSentryFromFeatureFlags({
    featureFlags: ffData,
  });
}

root.render(
  <FeatureFlagsContext.Provider value={ffData}>
    <BrowserGuard>
      <Provider store={store}>
        <PersistGate loading={null} persistor={persistor}>
          <ThemeProvider theme={theme}>
            <ToastContainer
              position="top-center"
              autoClose={1000}
              hideProgressBar={true}
              newestOnTop={false}
              closeOnClick
              rtl={false}
              pauseOnFocusLoss
              transition={Slide}
              pauseOnHover
            />
            <ErrorHandler />
            <App initialFeatureFlags={ffData} />
          </ThemeProvider>
        </PersistGate>
      </Provider>
    </BrowserGuard>
  </FeatureFlagsContext.Provider>
);
